/**
 * Offer Management Types for Choice Energy Tendering System
 */

import { generateUUID } from '../utils/cryptoUtils';

// Ranked offer selection stored on the tender (one column, all sites' ranks together).
// siteId is captured once at selection time and never re-derived — each site gets its
// own independent rank 1/2/3, and this makes that membership permanent regardless of
// whether the offers list is correctly mapped/loaded at the moment of a later check.
//
// When every site on the tender shares the same ABN (one legal entity), ranking is
// SHARED instead — siteId is omitted and retailerName is set, so this selection applies
// to every site's own matching (retailer, term) offer rather than one specific site's
// offer row. Selections with a siteId (legacy / different-ABN-per-site tenders) keep
// today's per-site behaviour unchanged.
/**
 * A user's 1st/2nd/3rd choice pick.
 *
 * ⚠️ siteId and retailerName are BOTH always written now. They used to be mutually exclusive
 * — siteId only in per-site mode, retailerName only in shared mode — which made an entry
 * written under one mode invisible to the other mode's lookups while still occupying a rank
 * slot, so the UI reported "Maximum 3 offers selected" with no way to clear it. The mode
 * (allSitesSameAbn) is derived from asynchronously-loaded sites, so it genuinely flips during
 * a session and entries WILL be read back under the other mode. Keep both fields populated.
 *
 * Still optional on the type so selections persisted before this change still parse; they are
 * normalised on load (see CompareAndSelectStep's normaliseRankedSelections).
 */
export type RankedOfferSelection = {
  rank: 1 | 2 | 3;
  offerId: string;  // parent DB offer ID (not virtual) — whichever site's row was clicked
  termMonths: number;
  siteId?: string;       // frontend site.id this ranking belongs to
  retailerName?: string; // retailer name, for cross-site matching in shared mode
};

export type OfferType = 'electricity' | 'gas';
export type PricingComponent = 
  | 'peak_rate' | 'offpeak_rate' | 'shoulder_rate'
  | 'daily_supply' | 'demand_charge' | 'green_premium'
  | 'connection_fee' | 'exit_fee' | 'late_payment_fee'
  | 'network_charge' | 'environmental_charge' | 'market_charge';

export type SortOption =
  | 'total_cost' | 'savings' | 'peak_rate' | 'daily_supply'
  | 'green_percent' | 'retailer_name';

export type ViewMode = 'cards' | 'table' | 'comparison';

// Standard contract length options for recommendations
export const CONTRACT_LENGTHS = [12, 24, 36, 48, 60] as const;
export type ContractLength = typeof CONTRACT_LENGTHS[number];

// Extended contract terms that can appear in parsed offers (includes non-standard terms)
export const ALL_CONTRACT_TERMS = [10, 12, 18, 22, 24, 30, 36, 48, 60, 72] as const;
export type AllContractTerm = typeof ALL_CONTRACT_TERMS[number];

// Helper to get contract terms from an offer's multiYearRateData or parsed data
// DEPRECATED: Use getAvailableTerms from offerTermUtils.ts instead
export const getOfferContractTerms = (offer: any): number[] => {
  // Priority 1: extn.contract_terms — most authoritative source (set by offerAutoPopulateService)
  // Keys may be suffixed like "22_smooth", "46_stepped" — strip suffix and deduplicate
  const contractTerms = offer?.extn?.contract_terms ||
                        offer?.multiYearRateData?.contractTerms ||
                        offer?.multiYearRateData?.contract_terms ||
                        offer?.parsedData?.contractTerms ||
                        offer?.parsedData?.contract_terms;

  if (contractTerms && typeof contractTerms === 'object' && Object.keys(contractTerms).length > 0) {
    const fromKeys = Array.from(new Set(
      Object.keys(contractTerms)
        .map(key => parseInt(key.split('_')[0], 10))
        .filter(n => !isNaN(n) && n > 0)
    )).sort((a, b) => a - b);
    if (fromKeys.length > 0) return fromKeys;
  }

  // Priority 2: availableTerms column (camelCase React state or snake_case DB)
  const availTerms = offer?.availableTerms || offer?.available_terms;
  if (availTerms && Array.isArray(availTerms) && availTerms.length > 0) {
    return availTerms.map(Number).filter((n: number) => !isNaN(n) && n > 0).sort((a: number, b: number) => a - b);
  }

  // Fallback: standard terms
  return [...CONTRACT_LENGTHS];
};

// Helper to get available terms for selection — only shows terms the offer actually has
export const getAvailableTermsForOffer = (offer: any): number[] => {
  return getOfferContractTerms(offer);
};

export interface OfferFees {
  connectionFee?: number;
  exitFee?: number;
  latePaymentFee?: number;
  latePaymentPercent?: number;
  paperBillFee?: number;
  dishonourFee?: number;
  [key: string]: any;
}

export interface OfferLine {
  id: string;
  offerId: string;
  tenderSiteId?: string; // null = group line (applies to all sites)
  component: PricingComponent;
  unit: string; // kWh, kW, day, %, $
  rate: number;
  minQty?: number;
  maxQty?: number;
  notes?: string;
  isGroupRate: boolean; // true if applies to all sites
  extn?: Record<string, any>; // jsonb extension field
}

export interface Offer {
  id: string;
  orgId: string;
  tenderId: string;
  tenderSiteId?: string;  // NEW: Links offer to specific site
  siteId?: string;  // NEW: Reference to the site
  siteName?: string;  // NEW: Site name for display
  retailerId: string;
  retailerName: string;
  retailerLogo?: string;
  offerType: OfferType;
  validFrom?: Date;
  validTo?: Date;
  termMonths?: number; // Currently selected contract term for UI display
  greenPercent: number;
  feesJson: OfferFees;
  sourceFileId?: string;
  sourceCitations?: any;
  confidence: number; // 0-100, AI extraction confidence
  isRecommended: boolean; // Keep for backward compatibility
  recommendedMonths: number[]; // Array of months this offer is recommended for [12, 24, 36, 48]
  availableTerms?: number[]; // Dynamic contract terms from parsed data (e.g. [10, 12, 18, 22, 24, 30, 36])
  expiryDate?: string | Date | null; // Offer expiry date
  validityDate?: string | Date | null; // Offer validity date
  multiYearRateData?: MultiYearRateData; // Complete form data for persistence
  rationale?: string;
  estimatedAnnualCost?: number;
  estimatedSavings?: number;
  estimatedSavingsPercent?: number;
  offerLines: OfferLine[];
  createdAt: Date;
  updatedAt: Date;
  status: 'draft' | 'complete' | 'expired' | 'selected';
  notes?: string;
  extn?: Record<string, any>; // jsonb extension field
}

// Enhanced offer interface with multi-year support
export interface EnhancedOffer extends Offer {
  contractYears: 1 | 2 | 3 | 4;

  // Multi-year pricing rates
  peakRateMultiYear?: MultiYearRates;
  offpeakRateMultiYear?: MultiYearRates;
  shoulderRateMultiYear?: MultiYearRates;

  // Environmental and non-energy charges
  environmentalCharges?: EnvironmentalCharges;
  nonEnergyCharges?: NonEnergyCharges;

  // Enhanced retailer branding
  retailerBrandColors?: {
    primary: string;
    secondary: string;
  };
}

export interface OfferComparison {
  offers: Offer[];
  comparisonMetrics: {
    lowestCost: Offer;
    highestSavings: Offer;
    bestGreen: Offer;
    shortestTerm: Offer;
    longestTerm: Offer;
  };
  siteSpecificComparisons?: {
    siteId: string;
    siteName: string;
    comparisons: OfferLine[];
  }[];
}

export interface OfferFilters {
  retailers: string[];
  offerTypes: OfferType[];
  minSavings?: number;
  maxCost?: number;
  minGreenPercent?: number;
  termMonths?: number[];
  status?: string[];
  hasGroupRates?: boolean;
  hasSiteSpecificRates?: boolean;
}

export interface OfferMetrics {
  totalOffers: number;
  pendingReview: number;
  recommendedOffer?: Offer;
  averageAnnualCost: number;
  averageSavings: number;
  averageSavingsPercent: number;
  bestOffer?: Offer;
  processingCount: number;
}

export interface RecommendationEngine {
  recommendedOfferId?: string;
  confidence: number;
  rationale: string;
  strengths: string[];
  considerations: string[];
  alternativeOptions: {
    offerId: string;
    reason: string;
  }[];
}

// Validation interfaces
export interface OfferValidation {
  isValid: boolean;
  errors: {
    general?: string[];
    retailer?: string;
    pricing?: {
      [component: string]: string;
    };
    terms?: string[];
    fees?: string[];
  };
  warnings: string[];
}

// Multi-year rate structure for up to 6-year contracts (12, 24, 36, 48, 60, 72 months)
export interface MultiYearRates {
  year_1: number; // 12 months
  year_2: number; // 24 months
  year_3: number; // 36 months
  year_4: number; // 48 months
  year_5: number; // 60 months
  year_6: number; // 72 months
}

// ============================================================================
// NEW: Contract Term Based Rate Structure
// Used for parsed offer data where rates are organized by contract term
// ============================================================================

/**
 * Rate data for a single year within a contract term
 * Contains peak, offpeak, and optional shoulder rates
 * May include date ranges and additional dynamic fields
 */
export interface YearRateData {
  peak: number;
  offpeak: number;
  shoulder: number | null; // null means use peak as fallback
  dateRange?: {
    startDate?: string | null; // YYYY-MM-DD format
    endDate?: string | null; // YYYY-MM-DD format
  };
  [key: string]: any; // Allow additional dynamic fields
}

/**
 * Contract term rate structure
 * Key is year number (year_1, year_2, etc.)
 * 12-month contract has year_1
 * 24-month contract has year_1 and year_2
 * 36-month contract has year_1, year_2, year_3
 * 48-month contract has year_1, year_2, year_3, year_4
 */
export interface ContractTermRates {
  year_1?: YearRateData;
  year_2?: YearRateData;
  year_3?: YearRateData;
  year_4?: YearRateData;
  year_5?: YearRateData;
  year_6?: YearRateData;
  dateRange?: {
    startDate?: string | null; // Overall contract term date range
    endDate?: string | null;
  };
  [key: string]: any; // Allow additional dynamic fields per contract term
}

/**
 * Parsed environmental charges (simple structure, not per-year)
 */
export interface ParsedEnvironmentalCharge {
  ratePerKwh: number | null;
  powerPercentage: number | null;
  pricePerCertificate: number | null;
}

/**
 * Parsed offer data structure from document parsing
 * Organized by contract term for easy display and editing
 */
export interface ParsedOfferData {
  expiryDate: string | null;
  validityDate: string | null;
  contractTerms: {
    [termMonths: string]: ContractTermRates; // "12", "24", "36", "48"
  };
  nonEnergyCharges: {
    "Market Fees"?: number | null;
    "Meter Agreement"?: number | null;
    "Other Service Charge"?: number | null;
    "Supplementary Metering"?: number | null;
    [key: string]: number | null | undefined;
  };
  environmentalCharges: {
    LREC?: ParsedEnvironmentalCharge;
    SREC?: ParsedEnvironmentalCharge;
    "VEEC/ESS/REES"?: ParsedEnvironmentalCharge;
  };
  notes?: {
    [key: string]: any; // Dynamic notes and additional information that varies by offer
  };
  [key: string]: any; // Allow additional top-level dynamic fields
}

// Contract term options for UI display
export const CONTRACT_TERM_OPTIONS = [12, 24, 36, 48] as const;
export type ContractTermOption = typeof CONTRACT_TERM_OPTIONS[number];

// Environmental charge data for each year (Price Per Certificate, Power %, Rate Per kWh)
export interface EnvironmentalChargeYearData {
  pricePerCertificate: number; // Price per certificate (usually 0 or "–")
  powerPercentage: number; // Power percentage (e.g., 17.91 for 17.91%)
  ratePerKwh: number; // Rate per kWh (e.g., 0.00000000)
}

// Environmental charges structure with LREC, SREC, and VEEC/ESS/REES
export interface EnvironmentalCharges {
  LREC: Record<string, EnvironmentalChargeYearData>;
  SREC: Record<string, EnvironmentalChargeYearData>;
  "VEEC/ESS/REES": Record<string, EnvironmentalChargeYearData>;
  [key: string]: Record<string, EnvironmentalChargeYearData>;
}

// Non-energy charges structure - single common values (not per year)
export interface NonEnergyCharges {
  "Meter Agreement"?: number; // Single common value
  "Supplementary Metering"?: number; // Single common value
  [customChargeName: string]: number | undefined; // Custom charges as single values
}

// Rate type for multi-year progression
export type RateType = 'smooth' | 'stepped';

// Network charge rates for a single year (VIC distributor pass-through charges)
export interface NetworkChargeRates {
  winter_peak_rate: number;    // c/kWh (applied to annual_kwh_peak)
  winter_offpeak_rate: number; // c/kWh (applied to annual_kwh_offpeak)
  capacity_rate: number;       // c/kW/year (applied to peak_demand_kw)
  supply_charge_rate: number;  // c/site/year (flat annual)
}

// Per-year network charges (year_1, year_2, year_3, year_4)
export interface NetworkChargesByYear {
  year_1: NetworkChargeRates;
  year_2: NetworkChargeRates;
  year_3: NetworkChargeRates;
  year_4: NetworkChargeRates;
  year_5: NetworkChargeRates;
  year_6: NetworkChargeRates;
}

// Other/retailer-specific charges for a single year
export interface OtherChargeRates {
  meter_supply_rate: number;      // c/kWh (applied to annual_kwh_peak)
  service_charge_rate: number;    // c/kWh (applied to annual_kwh_offpeak)
  aemo_participant_rate: number;  // c/kW/year (applied to peak_demand_kw)
  aemo_ancillary_rate: number;    // c/site/year (flat annual)
}

// Per-year other charges
export interface OtherChargesByYear {
  year_1: OtherChargeRates;
  year_2: OtherChargeRates;
  year_3: OtherChargeRates;
  year_4: OtherChargeRates;
  year_5: OtherChargeRates;
  year_6: OtherChargeRates;
}

// Complete rate data structure for each rate type
export interface RateTypeData {
  peakRates: MultiYearRates;
  offpeakRates: MultiYearRates;
  shoulderRates: MultiYearRates;
  environmentalCharges: EnvironmentalCharges;
  nonEnergyCharges: NonEnergyCharges;
  networkCharges?: Partial<NetworkChargesByYear>;   // Network charges per year (optional)
  otherCharges?: Partial<OtherChargesByYear>;       // Other charges per year (optional)
  greenPremiumRates?: MultiYearRates;               // Green premium rate per year (c/kWh)
}

// Rate data storage for both smooth and stepped
export interface MultiYearRateData {
  smooth: RateTypeData;
  stepped: RateTypeData;
  contract_terms?: {
    [termMonths: string]: {
      year_1?: {
        peak?: number;
        offpeak?: number;
        shoulder?: number;
        date_range?: {
          start_date: string;
          end_date: string;
        };
      };
      year_2?: {
        peak?: number;
        offpeak?: number;
        shoulder?: number;
        date_range?: {
          start_date: string;
          end_date: string;
        };
      };
      year_3?: {
        peak?: number;
        offpeak?: number;
        shoulder?: number;
        date_range?: {
          start_date: string;
          end_date: string;
        };
      };
      year_4?: {
        peak?: number;
        offpeak?: number;
        shoulder?: number;
        date_range?: {
          start_date: string;
          end_date: string;
        };
      };
      year_5?: {
        peak?: number;
        offpeak?: number;
        shoulder?: number;
        date_range?: {
          start_date: string;
          end_date: string;
        };
      };
      year_6?: {
        peak?: number;
        offpeak?: number;
        shoulder?: number;
        date_range?: {
          start_date: string;
          end_date: string;
        };
      };
      date_range?: {
        start_date: string;
        end_date: string;
      };
      [key: string]: any; // Allow additional dynamic keys
    };
  };
}

// Enhanced retailer interface with branding
export interface EnergyRetailer {
  id: string;
  name: string;
  logo: string;
  brandColors: {
    primary: string;
    secondary: string;
  };
  description: string;
}

// Australian energy retailers with enhanced branding
export const ENERGY_RETAILERS: EnergyRetailer[] = [
  {
    id: 'agl',
    name: 'AGL Energy',
    logo: '/logos/AGLEnergy.jpg',
    brandColors: { primary: '#FF6B35', secondary: '#004225' },
    description: 'Australia\'s leading integrated energy company'
  },
  {
    id: 'origin',
    name: 'Origin Energy',
    logo: '/logos/origin.jpg',
    brandColors: { primary: '#00A651', secondary: '#0066CC' },
    description: 'Powering Australian homes and businesses'
  },
  {
    id: 'energyaustralia',
    name: 'EnergyAustralia',
    logo: '/logos/energyAustralia.png',
    brandColors: { primary: '#E31E24', secondary: '#1E1E1E' },
    description: 'Reliable energy solutions for Australia'
  },
  {
    id: 'red',
    name: 'Red Energy',
    logo: '/logos/redEnergy.png',
    brandColors: { primary: '#E31E24', secondary: '#FFFFFF' },
    description: '100% Australian owned energy supplier'
  },
  {
    id: 'alinta',
    name: 'Alinta Energy',
    logo: '/logos/alinta.png',
    brandColors: { primary: '#0066CC', secondary: '#FF6B35' },
    description: 'Competitive energy solutions'
  },
  {
    id: 'simply',
    name: 'Simply Energy',
    logo: '/logos/simple.png',
    brandColors: { primary: '#00A651', secondary: '#1E1E1E' },
    description: 'Simple, fair energy pricing'
  },
  {
    id: 'momentum',
    name: 'Momentum Energy',
    logo: '/logos/momentum.jpg',
    brandColors: { primary: '#FF6B35', secondary: '#0066CC' },
    description: 'Momentum towards a cleaner future'
  },
  {
    id: 'powershop',
    name: 'Powershop',
    logo: '/choiceEnergyLogo.png',
    brandColors: { primary: '#00A651', secondary: '#FF6B35' },
    description: 'Power to choose, power to save'
  },
  {
    id: 'lumo',
    name: 'Lumo Energy',
    logo: '/choiceEnergyLogo.png',
    brandColors: { primary: '#E31E24', secondary: '#0066CC' },
    description: 'Bright energy solutions'
  },
  {
    id: 'globird',
    name: 'GloBird Energy',
    logo: '/choiceEnergyLogo.png',
    brandColors: { primary: '#0066CC', secondary: '#00A651' },
    description: 'Global energy, local service'
  },
];

// Pricing component definitions
export const PRICING_COMPONENTS = [
  { 
    id: 'peak_rate', 
    name: 'Peak Rate', 
    unit: 'c/kWh', 
    description: 'Rate during peak hours (typically 7am-11pm weekdays)',
    category: 'usage'
  },
  { 
    id: 'offpeak_rate', 
    name: 'Off-Peak Rate', 
    unit: 'c/kWh', 
    description: 'Rate during off-peak hours (typically 11pm-7am, weekends)',
    category: 'usage'
  },
  { 
    id: 'shoulder_rate', 
    name: 'Shoulder Rate', 
    unit: 'c/kWh', 
    description: 'Rate during shoulder periods (varies by network)',
    category: 'usage'
  },
  { 
    id: 'daily_supply', 
    name: 'Daily Supply Charge', 
    unit: 'c/day', 
    description: 'Fixed daily charge regardless of usage',
    category: 'fixed'
  },
  { 
    id: 'demand_charge', 
    name: 'Demand Charge', 
    unit: '$/kW', 
    description: 'Charge based on maximum demand',
    category: 'demand'
  },
  { 
    id: 'green_premium', 
    name: 'Green Energy Premium', 
    unit: 'c/kWh', 
    description: 'Additional charge for renewable energy',
    category: 'environmental'
  },
  { 
    id: 'connection_fee', 
    name: 'Connection Fee', 
    unit: '$', 
    description: 'One-time connection charge',
    category: 'fees'
  },
  { 
    id: 'exit_fee', 
    name: 'Exit Fee', 
    unit: '$', 
    description: 'Fee for early contract termination',
    category: 'fees'
  },
];

// Default offer template
export const createDefaultOffer = (tenderId?: string, retailerId?: string): Partial<Offer> => {
  const retailer = ENERGY_RETAILERS.find(r => r.id === retailerId);
  const offerId = generateUUID();

  // Create default offer lines for 6-year structure
  const defaultOfferLines: OfferLine[] = [
    // Peak rates for 6 years
    { id: generateUUID(), offerId, component: 'peak_rate', unit: 'c/kWh', rate: 25.0, isGroupRate: true, notes: 'Year 1 Peak Rate' },
    { id: generateUUID(), offerId, component: 'peak_rate', unit: 'c/kWh', rate: 26.0, isGroupRate: true, notes: 'Year 2 Peak Rate' },
    { id: generateUUID(), offerId, component: 'peak_rate', unit: 'c/kWh', rate: 27.0, isGroupRate: true, notes: 'Year 3 Peak Rate' },
    { id: generateUUID(), offerId, component: 'peak_rate', unit: 'c/kWh', rate: 28.0, isGroupRate: true, notes: 'Year 4 Peak Rate' },
    { id: generateUUID(), offerId, component: 'peak_rate', unit: 'c/kWh', rate: 29.0, isGroupRate: true, notes: 'Year 5 Peak Rate' },
    { id: generateUUID(), offerId, component: 'peak_rate', unit: 'c/kWh', rate: 30.0, isGroupRate: true, notes: 'Year 6 Peak Rate' },

    // Off-peak rates for 6 years
    { id: generateUUID(), offerId, component: 'offpeak_rate', unit: 'c/kWh', rate: 18.0, isGroupRate: true, notes: 'Year 1 Off-Peak Rate' },
    { id: generateUUID(), offerId, component: 'offpeak_rate', unit: 'c/kWh', rate: 19.0, isGroupRate: true, notes: 'Year 2 Off-Peak Rate' },
    { id: generateUUID(), offerId, component: 'offpeak_rate', unit: 'c/kWh', rate: 20.0, isGroupRate: true, notes: 'Year 3 Off-Peak Rate' },
    { id: generateUUID(), offerId, component: 'offpeak_rate', unit: 'c/kWh', rate: 21.0, isGroupRate: true, notes: 'Year 4 Off-Peak Rate' },
    { id: generateUUID(), offerId, component: 'offpeak_rate', unit: 'c/kWh', rate: 22.0, isGroupRate: true, notes: 'Year 5 Off-Peak Rate' },
    { id: generateUUID(), offerId, component: 'offpeak_rate', unit: 'c/kWh', rate: 23.0, isGroupRate: true, notes: 'Year 6 Off-Peak Rate' },

    // Daily supply charges for 6 years
    { id: generateUUID(), offerId, component: 'daily_supply', unit: 'c/day', rate: 95.0, isGroupRate: true, notes: 'Year 1 Daily Supply' },
    { id: generateUUID(), offerId, component: 'daily_supply', unit: 'c/day', rate: 98.0, isGroupRate: true, notes: 'Year 2 Daily Supply' },
    { id: generateUUID(), offerId, component: 'daily_supply', unit: 'c/day', rate: 101.0, isGroupRate: true, notes: 'Year 3 Daily Supply' },
    { id: generateUUID(), offerId, component: 'daily_supply', unit: 'c/day', rate: 104.0, isGroupRate: true, notes: 'Year 4 Daily Supply' },
    { id: generateUUID(), offerId, component: 'daily_supply', unit: 'c/day', rate: 107.0, isGroupRate: true, notes: 'Year 5 Daily Supply' },
    { id: generateUUID(), offerId, component: 'daily_supply', unit: 'c/day', rate: 110.0, isGroupRate: true, notes: 'Year 6 Daily Supply' },
  ];

  // Create default fees structure
  const defaultFeesJson: OfferFees = {
    environmentalCharges: {
      lret: 2.5,  // LRET rate
      veec: 1.8,  // VEEC rate
      sres: 3.2,  // SRES rate
    },
    nonEnergyCharges: {
      meterAgreement: 120.0,        // Annual meter agreement fee
      supplementaryMetering: 85.0,   // Annual supplementary metering fee
    },
    connectionFee: 150.0,  // One-time connection fee
  };

  return {
    tenderId: tenderId || '',
    retailerId: retailerId || '',
    retailerName: retailer?.name || '',
    retailerLogo: retailer?.logo,
    offerType: 'electricity',
    availableTerms: [12, 24, 36, 48, 60, 72], // Default available contract terms
    greenPercent: 0,
    feesJson: defaultFeesJson,
    confidence: 100, // Manual entry = 100% confidence
    isRecommended: false,
    recommendedMonths: [],
    offerLines: defaultOfferLines,
    status: 'draft',
    notes: '',
    extn: {},
  };
};

// Offer validation utilities - DISABLED: No mandatory fields for now
export const validateOffer = (offer: Offer): OfferValidation => {
  const errors: OfferValidation['errors'] = {};
  const warnings: string[] = [];
  let isValid = true;

  // All validations disabled - users can create offers without any data
  // This can be re-enabled in the future when mandatory fields are needed

  // Commented out all validation logic:
  /*
  if (!offer.retailerName?.trim()) {
    errors.retailer = 'Retailer name is required';
    isValid = false;
  }
  if (!offer.termMonths || offer.termMonths < 1) {
    errors.terms = ['Contract term must be at least 1 month'];
    isValid = false;
  } else if (offer.termMonths > 60) {
    warnings.push('Contract term is unusually long (>5 years)');
  }
  if (offer.greenPercent < 0 || offer.greenPercent > 100) {
    errors.general = errors.general || [];
    errors.general.push('Green energy percentage must be between 0-100%');
    isValid = false;
  }
  const pricingErrors: { [component: string]: string } = {};
  offer.offerLines.forEach(line => {
    if (line.rate < 0) {
      pricingErrors[line.component] = 'Rate cannot be negative';
      isValid = false;
    }
    if (line.component.includes('rate') && line.rate > 100) {
      warnings.push(`${line.component} rate seems unusually high (>${line.rate}c/kWh)`);
    }
  });
  if (Object.keys(pricingErrors).length > 0) {
    errors.pricing = pricingErrors;
  }
  const hasUsageRate = offer.offerLines.some(line =>
    ['peak_rate', 'offpeak_rate'].includes(line.component)
  );
  const hasDailySupply = offer.offerLines.some(line =>
    line.component === 'daily_supply'
  );
  if (!hasUsageRate) {
    errors.pricing = errors.pricing || {};
    errors.pricing.usage = 'At least one usage rate (peak or off-peak) is required';
    isValid = false;
  }
  if (!hasDailySupply) {
    warnings.push('No daily supply charge specified');
  }
  */

  return { isValid: true, errors: {}, warnings: [] }; // Always return valid - no mandatory fields
};

// Calculate estimated annual cost
export const calculateEstimatedCost = (
  offer: Offer, 
  annualUsageKwh: number = 5000,
  peakUsagePercent: number = 0.6
): number => {
  let totalCost = 0;

  offer.offerLines.forEach(line => {
    switch (line.component) {
      case 'peak_rate':
        totalCost += (line.rate / 100) * annualUsageKwh * peakUsagePercent;
        break;
      case 'offpeak_rate':
        totalCost += (line.rate / 100) * annualUsageKwh * (1 - peakUsagePercent);
        break;
      case 'daily_supply':
        totalCost += (line.rate / 100) * 365;
        break;
      case 'connection_fee':
        totalCost += line.rate;
        break;
      // Add other components as needed
    }
  });

  // Add fees
  if (offer.feesJson.connectionFee) {
    totalCost += offer.feesJson.connectionFee;
  }

  return Math.round(totalCost * 100) / 100; // Round to 2 decimal places
};

// Default empty multi-year rates
export const createEmptyMultiYearRates = (): MultiYearRates => ({
  year_1: 0, // 12 months
  year_2: 0, // 24 months
  year_3: 0, // 36 months
  year_4: 0, // 48 months
  year_5: 0, // 60 months
  year_6: 0, // 72 months
});

// Default empty environmental charge year data
export const createEmptyEnvironmentalChargeYearData = (): EnvironmentalChargeYearData => ({
  pricePerCertificate: 0,
  powerPercentage: 0,
  ratePerKwh: 0,
});

// Default empty environmental charges
export const createEmptyEnvironmentalCharges = (): EnvironmentalCharges => ({
  LREC: {
    year_1: createEmptyEnvironmentalChargeYearData(),
    year_2: createEmptyEnvironmentalChargeYearData(),
    year_3: createEmptyEnvironmentalChargeYearData(),
    year_4: createEmptyEnvironmentalChargeYearData(),
    year_5: createEmptyEnvironmentalChargeYearData(),
    year_6: createEmptyEnvironmentalChargeYearData(),
  },
  SREC: {
    year_1: createEmptyEnvironmentalChargeYearData(),
    year_2: createEmptyEnvironmentalChargeYearData(),
    year_3: createEmptyEnvironmentalChargeYearData(),
    year_4: createEmptyEnvironmentalChargeYearData(),
    year_5: createEmptyEnvironmentalChargeYearData(),
    year_6: createEmptyEnvironmentalChargeYearData(),
  },
  "VEEC/ESS/REES": {
    year_1: createEmptyEnvironmentalChargeYearData(),
    year_2: createEmptyEnvironmentalChargeYearData(),
    year_3: createEmptyEnvironmentalChargeYearData(),
    year_4: createEmptyEnvironmentalChargeYearData(),
    year_5: createEmptyEnvironmentalChargeYearData(),
    year_6: createEmptyEnvironmentalChargeYearData(),
  },
});

// Default empty non-energy charges
export const createEmptyNonEnergyCharges = (): NonEnergyCharges => ({
  "Meter Agreement": 0,
  "Supplementary Metering": 0,
});

// Default empty network charge rates (one year)
export const createEmptyNetworkChargeRates = (): NetworkChargeRates => ({
  winter_peak_rate: 0,
  winter_offpeak_rate: 0,
  capacity_rate: 0,
  supply_charge_rate: 0,
});

// Default empty other charge rates (one year)
export const createEmptyOtherChargeRates = (): OtherChargeRates => ({
  meter_supply_rate: 0,
  service_charge_rate: 0,
  aemo_participant_rate: 0,
  aemo_ancillary_rate: 0,
});

// Default empty network charges by year
export const createEmptyNetworkChargesByYear = (): NetworkChargesByYear => ({
  year_1: createEmptyNetworkChargeRates(),
  year_2: createEmptyNetworkChargeRates(),
  year_3: createEmptyNetworkChargeRates(),
  year_4: createEmptyNetworkChargeRates(),
  year_5: createEmptyNetworkChargeRates(),
  year_6: createEmptyNetworkChargeRates(),
});

// Default empty other charges by year
export const createEmptyOtherChargesByYear = (): OtherChargesByYear => ({
  year_1: createEmptyOtherChargeRates(),
  year_2: createEmptyOtherChargeRates(),
  year_3: createEmptyOtherChargeRates(),
  year_4: createEmptyOtherChargeRates(),
  year_5: createEmptyOtherChargeRates(),
  year_6: createEmptyOtherChargeRates(),
});
